'use client';
import dynamic from 'next/dynamic';
import { Suspense, useEffect, useState } from 'react';

import type { TSubscriptionItem } from '~entities/subscription';
import annalsSubscription from '~features/annals/hooks/subscription';
import { useLanguage } from '~features/language';
import { Toaster } from '~features/toaster/Toaster';
import ClientLogger from '~logger/client';
import { LoggerMessages, LoggerServices } from '~logger/constants';
import { getFormattedDate } from '~shared/utils/datesIntl';

import { useOffer } from '../../model/useOffer';

const MainModal = dynamic(
	() => import('../MainModal').then((mod) => mod.MainModal),
	{ ssr: false }
);
const Feedback = dynamic(
	() => import('../Feedback').then((mod) => mod.Feedback),
	{ ssr: false }
);
const ReasonsModal = dynamic(
	() => import('../ReasonsModal').then((mod) => mod.ReasonsModal),
	{ ssr: false }
);
const ConfirmModal = dynamic(
	() => import('../ConfirmModal').then((mod) => mod.ConfirmModal),
	{ ssr: false }
);
const OfferModal = dynamic(
	() => import('../OfferModal').then((mod) => mod.OfferModal),
	{ ssr: false }
);
const PromptModal = dynamic(
	() => import('../PromptModal').then((mod) => mod.PromptModal),
	{ ssr: false }
);
const CanceledModal = dynamic(
	() => import('../CanceledModal').then((mod) => mod.CanceledModal),
	{ ssr: false }
);

const OFFER_PROMOCODE = 'CHANCE70';
const RESUBSCRIPTION_PROMOCODE = 'BACK70';

const isCanceled = (subscription: TSubscriptionItem | undefined) =>
	subscription?.cancelDate;

type Props = {
	activeSubscription: TSubscriptionItem | undefined;
	onClose: () => void;
};

type Screen =
	| 'main'
	| 'feedback'
	| 'reasons'
	| 'confirm' // benefits
	| 'offer'
	| 'prompt'
	| 'canceled';

export const MySubscriptions = ({ activeSubscription, onClose }: Props) => {
	const { locale } = useLanguage();

	const [screen, setScreen] = useState<Screen>(
		isCanceled(activeSubscription) ? 'canceled' : 'main'
	);
	const [reasons, setReasons] = useState('');

	const { offer, isOfferUnreliable, discount, billPeriod, loading, validate } =
		useOffer({
			promocode:
				screen === 'canceled' ? RESUBSCRIPTION_PROMOCODE : OFFER_PROMOCODE,
			sku: activeSubscription?.sku,
			skip: screen !== 'confirm' && screen !== 'canceled',
		});

	const endDateLong = getFormattedDate({
		date: activeSubscription?.endDate,
		locale,
		month: 'long',
	});
	const endDateShort = getFormattedDate({
		date: activeSubscription?.endDate,
		locale,
	});

	const trackMainModalOpen = annalsSubscription.useViewModalPremiumActive();

	useEffect(() => {
		if (screen === 'main') {
			trackMainModalOpen();
		}
		// Трекаем только при открытии. Если попали с навигацией, то трекать не надо
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (screen === 'confirm' || screen === 'canceled') {
			void validate({
				PromoCode:
					screen === 'confirm' ? OFFER_PROMOCODE : RESUBSCRIPTION_PROMOCODE,
				skipSubscriptionCheck: true,
			});
		}
	}, [screen, validate]);

	useEffect(() => {
		if (screen === 'offer' && isOfferUnreliable) {
			setScreen('prompt');
		}
	}, [screen, isOfferUnreliable]);

	return (
		<Suspense>
			{(() => {
				if (screen === 'main') {
					return (
						<MainModal
							endDate={endDateLong}
							onClose={onClose}
							onFeedback={() => setScreen('feedback')}
							onCancel={() => setScreen('reasons')}
						/>
					);
				}

				if (screen === 'feedback') {
					return <Feedback open={true} onClose={() => setScreen('main')} />;
				}

				if (screen === 'reasons') {
					return (
						<ReasonsModal
							onClose={onClose}
							onSubmit={(val) => {
								setReasons(val);
								setScreen('confirm');
							}}
						/>
					);
				}

				if (screen === 'confirm') {
					return (
						<ConfirmModal
							onClose={onClose}
							onConfirm={() =>
								setScreen(isOfferUnreliable ? 'prompt' : 'offer')
							}
						/>
					);
				}

				if (screen === 'offer') {
					return (
						<OfferModal
							endDate={endDateShort}
							discount={discount}
							offer={offer}
							promocode={OFFER_PROMOCODE}
							billPeriod={billPeriod}
							loading={loading}
							locale={locale}
							onClose={onClose}
							onDecline={() => setScreen('prompt')}
						/>
					);
				}

				if (screen === 'prompt') {
					return (
						<PromptModal
							reasons={reasons}
							endDate={endDateLong}
							onClose={onClose}
							onConfirm={() => setScreen('canceled')}
						/>
					);
				}

				if (screen === 'canceled') {
					return (
						<CanceledModal
							endDate={endDateShort}
							endDateLong={endDateLong}
							promocode={RESUBSCRIPTION_PROMOCODE}
							offer={offer}
							discount={discount}
							billPeriod={billPeriod}
							locale={locale}
							loading={loading}
							onClose={onClose}
						/>
					);
				}

				return <FailedOrchestration screen={screen} onClose={onClose} />;
			})()}
		</Suspense>
	);
};

const FailedOrchestration = ({
	onClose,
	screen,
}: {
	onClose: () => void;
	screen: Screen;
}) => {
	useEffect(() => {
		ClientLogger.error(
			LoggerServices.MySubscriptions,
			LoggerMessages.OrchestrationError,
			{
				screen,
			}
		);
		Toaster.showError({ textKey: 'generalErrorMessage' });
		onClose();
	}, [onClose, screen]);

	return null;
};
